import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUserMessages, markAllMessagesAsRead } from '../utils/messagesAPI';
import TabBar from '../components/TabBar';
import Header from '../components/Header';
import { Bell, Pen, Trophy, Mail, Inbox, ChevronRight, Check } from 'lucide-react';

/**
 * 消息通知页面组件
 * 显示用户收到的所有消息通知
 */
function Messages({ session }) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // 获取消息列表
  useEffect(() => {
    const fetchMessages = async () => {
      try {
        setLoading(true);
        
        // 使用更新后的messagesAPI函数获取消息，不需要用户ID
        const messages = await getUserMessages();
        
        if (messages) {
          setMessages(messages);
          // 计算未读消息数量
          setUnreadCount(messages.filter(msg => !msg.is_read).length);
        }
      } catch (error) {
        console.error('获取消息列表出错:', error.message);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchMessages();
  }, []);

  // 获取消息图标
  const getIconForMessageType = (type) => {
    switch (type) {
      case 'system':
        return <Bell className="text-blue-500" />;
      case 'exam':
        return <Pen className="text-purple-500" />;
      case 'achievement':
        return <Trophy className="text-yellow-500" />;
      default:
        return <Mail className="text-gray-500" />;
    }
  };

  // 处理消息点击
  const handleMessageClick = (message) => {
    navigate(`/message-detail/${message.id}`);
  };
  
  // 标记所有消息为已读
  const handleMarkAllAsRead = async () => {
    try {
      const { success } = await markAllMessagesAsRead();
      if (success) {
        // 更新本地状态
        setMessages(prevMessages => 
          prevMessages.map(msg => ({...msg, is_read: true}))
        );
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('标记所有消息为已读出错:', error);
    }
  };
  
  // 格式化日期
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      // 今天，显示时间
      return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return '昨天';
    } else if (diffDays < 7) {
      return `${diffDays}天前`;
    } else {
      return date.toLocaleDateString('zh-CN', { month: 'long', day: 'numeric' });
    }
  };

  return (
    <div className="pb-24">
      <Header title="消息通知" />
      
      <div className="p-4">
        {loading ? (
          <div className="bg-white rounded-2xl shadow-md p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mx-auto"></div>
            <p className="mt-3 text-gray-500">加载中...</p>
          </div>
        ) : error ? (
          <div className="bg-white rounded-2xl shadow-md p-8 text-center">
            <p className="text-red-500">{error}</p>
            <button 
              className="mt-3 px-4 py-2 bg-blue-100 text-blue-600 rounded-lg"
              onClick={() => window.location.reload()}
            >
              重试
            </button>
          </div>
        ) : messages.length > 0 ? (
          <div>
            {unreadCount > 0 && (
              <div className="mb-3 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg flex justify-between items-center">
                <span>您有 {unreadCount} 条未读消息</span>
                <button 
                  onClick={handleMarkAllAsRead}
                  className="flex items-center text-sm text-blue-700 py-1 px-3 rounded-full bg-blue-100 hover:bg-blue-200"
                >
                  <Check className="w-3 h-3 mr-1" />
                  全部已读
                </button>
              </div>
            )}
            
            <div className="bg-white rounded-2xl shadow-md overflow-hidden">
              {messages.map((message) => (
                <div 
                  key={message.id}
                  onClick={() => handleMessageClick(message)}
                  className={`p-4 border-b border-gray-100 last:border-0 flex items-start ${
                    !message.is_read ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mr-3">
                    {getIconForMessageType(message.type)}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex justify-between items-center mb-1">
                      <h3 className={`font-medium ${!message.is_read ? 'text-blue-800' : 'text-gray-800'}`}>
                        {message.title}
                      </h3>
                      <div className="flex items-center">
                        <span className="text-xs text-gray-500 mr-1">{formatDate(message.created_at)}</span>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    </div>
                    
                    <p className="text-sm text-gray-600 mb-1 line-clamp-2">
                      {message.content}
                    </p>
                    
                    {!message.is_read && (
                      <span className="inline-block px-2 py-0.5 bg-red-100 text-red-600 text-xs rounded-full">
                        未读
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-2xl shadow-md">
            <Inbox className="w-16 h-16 text-gray-300 mx-auto mb-3" />
            <h3 className="text-xl font-bold text-gray-800 mb-2">暂无消息</h3>
            <p className="text-gray-600">
              目前没有任何通知。<br />
              新的消息和提醒会显示在这里。
            </p>
          </div>
        )}
      </div>
      
      <TabBar />
    </div>
  );
}

export default Messages; 